export type PaymentStage = {
  id: string;
  label: string;
  percent: number;
  timing: 'days' | 'months' | 'monthly' | 'specific-month' | 'possession';
  count?: number;
  month?: string;
};

export type LoanTrigger =
  | { kind: 'month'; month: string }
  | { kind: 'possession' }
  | { kind: 'after-dp' }
  | { kind: 'after-percent'; percent: number };

export type PaymentCondition = {
  version: 1;
  mode: 'basic' | 'advanced';
  stages: PaymentStage[];
  loan?: { requestedAmount?: number; requestedPercent?: number; trigger: LoanTrigger };
};

export const newPaymentCondition = (): PaymentCondition => ({
  version: 1,
  mode: 'basic',
  stages: [
    { id: 'dp', label: 'Down payment', percent: 0, timing: 'days', count: 0 },
    { id: 'remaining', label: 'Remaining payment', percent: 100, timing: 'monthly', count: 0 },
  ],
});

export const percentLabel = (value: number): string => `${Number(value.toFixed(2))}%`;
export const rupeeLabel = (value: number): string => `₹ ${value.toLocaleString('en-IN')}`;
export const loanRoadmapNote = 'Allottee committed to apply; loan may include furniture funding.';

export function loanRequirementLabel(loan: NonNullable<PaymentCondition['loan']>): string {
  return loan.requestedAmount != null && loan.requestedAmount > 0
    ? rupeeLabel(loan.requestedAmount)
    : loan.requestedPercent != null ? `${percentLabel(loan.requestedPercent)} (older plan)` : 'Amount needed';
}

export function validatePaymentCondition(value: PaymentCondition | null): string | null {
  if (!value) return 'Add payment conditions.';
  if (!value.stages.length) return 'Add at least one payment stage.';
  const total = value.stages.reduce((sum, stage) => sum + Math.round(Number(stage.percent) * 100), 0);
  if (total !== 10000) return `Payment stages must total 100% (currently ${percentLabel(total / 100)}).`;
  for (const stage of value.stages) {
    if (!stage.label.trim()) return 'Give each stage a name.';
    if (!Number.isFinite(stage.percent) || stage.percent <= 0 || stage.percent > 100) return 'Each stage needs a percentage above 0 and at most 100.';
    if (stage.timing === 'specific-month' && !/^\d{4}-(0[1-9]|1[0-2])$/.test(stage.month || '')) return 'Choose a payment month.';
    if (stage.timing !== 'possession' && stage.timing !== 'specific-month' && (!Number.isInteger(stage.count) || (stage.count || 0) < 1)) return 'Enter a positive whole number of days or months.';
    if (stage.timing === 'monthly' && Math.round(stage.percent * 100) < (stage.count || 0)) return 'Each monthly instalment must be at least 0.01%.';
  }
  if (value.loan) {
    const amount = value.loan.requestedAmount;
    const legacyPercent = value.loan.requestedPercent;
    if (amount == null && legacyPercent != null) {
      if (!Number.isFinite(legacyPercent) || legacyPercent <= 0 || legacyPercent > 100) return 'Enter a valid loan requirement.';
    } else if (!Number.isSafeInteger(amount) || (amount || 0) <= 0) return 'Enter the tentative loan amount in rupees.';
    if (value.loan.trigger.kind === 'month' && !/^\d{4}-(0[1-9]|1[0-2])$/.test(value.loan.trigger.month)) return 'Choose a loan application month.';
    if (value.loan.trigger.kind === 'after-percent' && (!Number.isFinite(value.loan.trigger.percent) || value.loan.trigger.percent <= 0 || value.loan.trigger.percent > 100)) return 'Enter a valid paid percentage for the loan trigger.';
  }
  return null;
}

const fromYmd = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

const addMonths = (date: Date, months: number): Date => {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), lastDay));
  return target;
};

const dateLabel = (date: Date): string => date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const monthLabel = (date: Date): string => date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

export type RoadmapItem = { id: string; label: string; percent: number; due: string; dueMonth?: string; dueDate?: string; instalments?: { label: string; percent: number; due: string; dueMonth?: string; dueDate?: string }[] };

const monthKey = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const ymdKey = (date: Date): string => `${monthKey(date)}-${String(date.getDate()).padStart(2, '0')}`;

export function buildPaymentRoadmap(condition: PaymentCondition, bookingDate: string): RoadmapItem[] {
  const bookedOn = fromYmd(bookingDate);
  let cursor = bookedOn;
  return condition.stages.map((stage) => {
    if (stage.timing === 'possession') return { id: stage.id, label: stage.label, percent: stage.percent, due: 'On possession' };
    if (stage.timing === 'specific-month') {
      const monthStart = fromYmd(`${stage.month || ''}-01`);
      if (monthStart) cursor = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      return { id: stage.id, label: stage.label, percent: stage.percent, due: monthStart ? `In ${monthLabel(monthStart)}` : 'In selected month', dueMonth: monthStart ? monthKey(monthStart) : undefined, dueDate: monthStart && cursor ? ymdKey(cursor) : undefined };
    }
    if (stage.timing === 'monthly') {
      const months = Math.max(1, stage.count || 1);
      const totalCents = Math.round(stage.percent * 100);
      const base = Math.floor(totalCents / months);
      const extra = totalCents % months;
      const instalments = Array.from({ length: months }, (_, index) => {
        const amount = base + (index < extra ? 1 : 0);
        const date = cursor ? addMonths(cursor, index + 1) : null;
        return { label: date ? monthLabel(date) : `Instalment ${index + 1}`, percent: amount / 100, due: date ? dateLabel(date) : `Month ${index + 1}`, dueMonth: date ? monthKey(date) : undefined, dueDate: date ? ymdKey(date) : undefined };
      });
      cursor = cursor ? addMonths(cursor, months) : null;
      return { id: stage.id, label: stage.label, percent: stage.percent, due: `Over ${months} months`, dueMonth: instalments[instalments.length - 1]?.dueMonth, instalments };
    }
    const count = stage.count || 1;
    cursor = cursor ? (stage.timing === 'days' ? new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + count) : addMonths(cursor, count)) : null;
    return { id: stage.id, label: stage.label, percent: stage.percent, due: cursor ? `By ${dateLabel(cursor)}` : `Within ${count} ${stage.timing}`, dueMonth: cursor ? monthKey(cursor) : undefined, dueDate: cursor ? ymdKey(cursor) : undefined };
  });
}

export function loanTriggerLabel(trigger: LoanTrigger): string {
  if (trigger.kind === 'month') {
    const date = fromYmd(`${trigger.month}-01`);
    return date ? `Apply in ${monthLabel(date)}` : 'Application month';
  }
  if (trigger.kind === 'possession') return 'Apply at possession';
  if (trigger.kind === 'after-dp') return 'Apply after down payment is received';
  return `Apply after ${percentLabel(trigger.percent)} has been paid`;
}

export function locateLoanTrigger(condition: PaymentCondition, roadmap: RoadmapItem[]): { stage: number; row: number } | null {
  const trigger = condition.loan?.trigger;
  if (!trigger) return null;
  if (trigger.kind === 'after-dp') return { stage: 0, row: -1 };
  if (trigger.kind === 'possession') {
    const stage = condition.stages.findIndex((item) => item.timing === 'possession');
    return stage < 0 ? null : { stage, row: -1 };
  }
  let paid = 0;
  for (let stage = 0; stage < roadmap.length; stage += 1) {
    const item = roadmap[stage];
    if (item.instalments) {
      for (let row = 0; row < item.instalments.length; row += 1) {
        paid += item.instalments[row].percent;
        if (trigger.kind === 'after-percent' && paid + 0.001 >= trigger.percent) return { stage, row };
        if (trigger.kind === 'month' && item.instalments[row].dueMonth && item.instalments[row].dueMonth! >= trigger.month) return { stage, row };
      }
    } else {
      paid += item.percent;
      if (trigger.kind === 'after-percent' && paid + 0.001 >= trigger.percent) return { stage, row: -1 };
      if (trigger.kind === 'month' && item.dueMonth && item.dueMonth >= trigger.month) return { stage, row: -1 };
    }
  }
  return null;
}

export type PaymentRoadmapStripCell = { percent: number; due: string; dueMonth?: string; stageIndex: number };

export function paymentRoadmapStripCells(roadmap: RoadmapItem[]): PaymentRoadmapStripCell[] {
  return roadmap.flatMap((item, stageIndex) =>
    item.instalments
      ? item.instalments.map((row) => ({ percent: row.percent, dueMonth: row.dueMonth, due: row.due, stageIndex }))
      : [{ percent: item.percent, dueMonth: item.dueMonth, due: item.due, stageIndex }]
  );
}

export function paymentRoadmapShortLabel(cell: PaymentRoadmapStripCell): string {
  if (cell.due === 'On possession') return 'Possession';
  const match = String(cell.dueMonth || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) return 'Due';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return `${date.toLocaleDateString('en-IN', { month: 'short' })} '${match[1].slice(-2)}`;
}

export function paymentConditionHtml(condition: PaymentCondition, bookingDate: string): string {
  const escape = (text: string) => text.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char));
  const roadmap = buildPaymentRoadmap(condition, bookingDate);
  const cells = paymentRoadmapStripCells(roadmap);
  const dpCount = cells.filter((cell) => cell.stageIndex === 0).length;
  const dpPercent = roadmap[0]?.percent || 0;
  const cellHtml = (cell: typeof cells[number], index: number, compact: boolean) =>
    `<td style="border:0.5px solid #a69f96;${index === dpCount ? 'border-left:1.5px solid #76644f;' : ''}padding:${compact ? '2px 1px' : '3px 2px'};text-align:center;vertical-align:middle;line-height:1.15;white-space:nowrap;overflow:hidden;">` +
    `<span style="display:block;font-size:${compact ? '7px' : '8px'};color:#555;">${escape(paymentRoadmapShortLabel(cell))}</span>` +
    `<strong style="display:block;margin-top:2px;font-size:${compact ? '9px' : '11px'};">${percentLabel(cell.percent)}</strong></td>`;
  const loanLine = condition.loan
    ? `<div style="border:0.5px solid #a69f96;border-top:0;padding:2px 5px;font-size:8px;line-height:1.2;color:#333;"><strong>LOAN · ${escape(loanRequirementLabel(condition.loan))}</strong> · ${escape(loanTriggerLabel(condition.loan.trigger))} · ${escape(loanRoadmapNote)}</div>`
    : '';
  const heading = `<div style="border:0.5px solid #a69f96;border-bottom:0;padding:2px 5px;font-size:8px;line-height:1.2;display:flex;justify-content:space-between;gap:5px;"><strong style="letter-spacing:.03em;">PAYMENT TERMS</strong>${cells.length > 12 ? `<span>DP ${percentLabel(dpPercent)} / Balance ${percentLabel(100 - dpPercent)}</span>` : ''}</div>`;
  const tableStyle = 'width:100%;border-collapse:collapse;table-layout:fixed;font-family:Arial,Helvetica,sans-serif;';
  let tables = '';
  if (cells.length <= 12) {
    tables = `<table style="${tableStyle}"><tbody><tr>` +
      `<th colspan="${dpCount}" style="border:0.5px solid #a69f96;background:#eee7dc;color:#443b31;font-size:8px;padding:2px;text-align:center;line-height:1.1;">DP · ${percentLabel(dpPercent)}</th>` +
      (cells.length > dpCount ? `<th colspan="${cells.length - dpCount}" style="border:0.5px solid #a69f96;border-left:1.5px solid #76644f;background:#f4f2ef;color:#443b31;font-size:8px;padding:2px;text-align:center;line-height:1.1;">REMAINING · ${percentLabel(100 - dpPercent)}</th>` : '') +
      `</tr><tr>${cells.map((cell, index) => cellHtml(cell, index, cells.length > 9)).join('')}</tr></tbody></table>`;
  } else {
    const rowCount = Math.ceil(cells.length / 12);
    const perRow = Math.ceil(cells.length / rowCount);
    for (let start = 0; start < cells.length; start += perRow) {
      tables += `<table style="${tableStyle}"><tbody><tr>${cells.slice(start, start + perRow).map((cell, index) => cellHtml(cell, start + index, true)).join('')}</tr></tbody></table>`;
    }
  }
  return `<div style="margin:-2mm 0 2mm;break-inside:avoid;page-break-inside:avoid;font-family:Arial,Helvetica,sans-serif;color:#111;">${heading}${tables}${loanLine}</div>`;
}
